/**
 * Utility functions for order processing
 */

// Format the order ID with a prefix
export function formatOrderId(id: string | number): string {
  if (typeof id === 'string' && id.startsWith('TK-')) {
    return id;
  }
  return `TK-${id}`;
}

// Get next order ID from the Supabase function or fallback to localStorage
export async function getNextOrderId(): Promise<string> {
  try {
    // First try to get from the database using the Supabase function
    const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
    const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;
    
    if (!supabaseUrl || !supabaseAnonKey) {
      throw new Error('Missing required environment variables: VITE_SUPABASE_URL or VITE_SUPABASE_ANON_KEY');
    }
    
    const functionUrl = `${supabaseUrl}/functions/v1/get-next-order-id`;
    console.log(`Calling function at: ${functionUrl}`);
    
    const response = await fetch(
      functionUrl,
      {
        headers: {
          'Authorization': `Bearer ${supabaseAnonKey}`,
          'Content-Type': 'application/json'
        },
        method: 'GET',
      }
    );

    if (!response.ok) {
      const errorText = await response.text();
      let errorData;
      try {
        errorData = JSON.parse(errorText);
      } catch {
        errorData = { message: errorText };
      }
      console.error('Server response:', response.status, response.statusText);
      console.error('Error details:', errorData);
      throw new Error(`Failed to get order ID from server: ${errorData.message || response.statusText}`);
    }

    const data = await response.json();
    console.log('Received order ID data:', data);
    return data.orderId;
  } catch (error) {
    console.error('Error getting order ID from server, falling back to localStorage:', error);
    
    try {
      // Fallback to localStorage if the function call fails
      const lastOrderId = localStorage.getItem('lastOrderId');
      const nextId = lastOrderId ? parseInt(lastOrderId) + 1 : 10000;
      localStorage.setItem('lastOrderId', nextId.toString());
      return formatOrderId(nextId);
    } catch (storageError) {
      console.error('Error accessing localStorage:', storageError);
      // Ultimate fallback to a random ID if even localStorage fails
      const randomId = 10000 + Math.floor(Math.random() * 90000);
      return formatOrderId(randomId);
    }
  }
}